"use client";

import { useEffect, useMemo, useState } from "react";
import { StatsigProvider, useClientAsyncInit, useStatsigUser } from "@statsig/react-bindings";
import { StatsigSessionReplayPlugin } from "@statsig/session-replay";
import { StatsigAutoCapturePlugin } from "@statsig/web-analytics";
import { useAuth } from "@/context/AuthContext";
import { toStatsigUser } from "@/lib/statsig/to-statsig-user";

const SDK_KEY = process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY ?? "";
const ENV_TIER = process.env.NEXT_PUBLIC_STATSIG_ENV ?? "development";

const STATSIG_PLUGINS = [
    new StatsigSessionReplayPlugin(),
    new StatsigAutoCapturePlugin(),
];

/**
 * Re-evaluates gates after Entra session hydrates. useClientAsyncInit only uses the
 * first render's user (anonymous) unless we call updateUserAsync.
 */
function StatsigAuthUserSync({ children }: { children: React.ReactNode }) {
    const { user, isLoading: authLoading } = useAuth();
    const { updateUserAsync } = useStatsigUser();
    const [syncedUserKey, setSyncedUserKey] = useState<string | null>(null);

    const statsigUser = useMemo(
        () => toStatsigUser({ id: user?.id, email: user?.email, tenantId: user?.tenantId }),
        [user],
    );
    const userKey = `${statsigUser.userID}:${statsigUser.email ?? ""}:${statsigUser.customIDs?.tenantID ?? ""}`;

    useEffect(() => {
        if (authLoading) return;

        let cancelled = false;
        updateUserAsync(statsigUser)
            .then(() => {
                if (!cancelled) setSyncedUserKey(userKey);
            })
            .catch(() => {
                if (!cancelled) setSyncedUserKey(userKey);
            });

        return () => {
            cancelled = true;
        };
    }, [authLoading, statsigUser, updateUserAsync, userKey]);

    if (authLoading || syncedUserKey !== userKey) {
        return null;
    }

    return <>{children}</>;
}

function StatsigProviderInner({ children }: { children: React.ReactNode }) {
    const { isLoading: authLoading } = useAuth();
    const bootstrapUser = useMemo(() => toStatsigUser(null), []);

    const { client } = useClientAsyncInit(SDK_KEY, bootstrapUser, {
        plugins: STATSIG_PLUGINS,
        environment: { tier: ENV_TIER },
    });

    if (authLoading) {
        return null;
    }

    return (
        <StatsigProvider client={client}>
            <StatsigAuthUserSync>{children}</StatsigAuthUserSync>
        </StatsigProvider>
    );
}

/**
 * Statsig client bootstrap (Track B4): session replay + auto-capture plugins.
 * Env-gated when `NEXT_PUBLIC_STATSIG_CLIENT_KEY` is unset.
 */
export function SaviraStatsigProvider({ children }: { children: React.ReactNode }) {
    if (!SDK_KEY) {
        return <>{children}</>;
    }

    return <StatsigProviderInner>{children}</StatsigProviderInner>;
}
